import { omit } from "lodash";
import { Plus } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  type InvoiceData,
  type LineItem,
  LineItemCreatedBy,
} from "@/apis/gql/types";
import { BookingsTableInstanceProvider } from "@/components/shared/bookings-table/bookings-table-instance-provider";
import { Table } from "@/components/shared/data-table";
import {
  ValidationErrorMessage,
  ValidationRoot,
} from "@/components/shared/form/shared/form-validation";
import { Button } from "@/components/ui/button";
import { TypographyLarge } from "@/components/ui/typography";
import { EVENT_NAMES } from "@/lib/events/names";
import {
  type BookingType,
  getBookingKey,
  groupByBooking,
} from "@/lib/miscellaneous/group-by-booking";
import { useTenant } from "@/providers/session";
import { useBookingTextStore } from "@/queries/fastlane/get-review";
import { useLazyGetAllLineItems } from "@/queries/line-items/fetch";
import { InvoiceModes } from "@/types/utils";

import {
  lineItemValidationSchema,
  lineItemsValidationSchema,
} from "../../../validations/line-item";
import { ReviewToolBar } from "../review-toolbar";

import { BackendSync } from "./backend-sync";
import { columns as _columns } from "./columns";
import { InvoiceBookingsActionBar } from "./invoice-bookings-action-bar";
import { useSortingOrderStore } from "./invoice-bookings-sorting-store";
import { InvoiceModeCard } from "./invoice-mode-card";
import { useBookingsReducer } from "./reducer";
import type { OperationAction } from "./types";

type Props = {
  invoice: {
    invoiceId: InvoiceData["invoiceId"];
    isNettoInvoice: InvoiceData["isNettoInvoice"];
    totalAmount: InvoiceData["totalAmount"];
    currency: InvoiceData["currency"];
    description: string;
    invoiceText: InvoiceData["invoiceText"];
  };
  onLineItemsChange: (lineItems: LineItem[]) => Promise<void> | void;
  onLoading: (loading: boolean) => void;
  readOnly?: boolean | string | React.ReactNode;
  disabled?: boolean;
};

export const InvoiceBookings = ({
  invoice,
  readOnly = false,
  disabled = false,
  onLineItemsChange,
  onLoading,
}: Props) => {
  const [bookings, setBookings] = useState<LineItem[]>([]);

  const {
    lineItemState,
    handleAddLineItem: _handleAddLineItem,
    getOperations,
    getLineItems,
    handleUpdateBookingAmount,
    handleUpdateBookingNettoAmount,
    handleUpdateAccountId,
    handleUpdateTaxCodeId,
    handleUpdateCostCenterId,
    handleUpdateBookingVatRate,
    handleDeleteLineItem,
    handleSetLineItems,
    handleSetOperations,
    handleUpdateLineItems,
    handleUnlink,
  } = useBookingsReducer();

  const [lineItems, setLineItems] = useState<LineItem[] | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const { fetch: fetchLineItems } = useLazyGetAllLineItems(
    {},
    { fetchPolicy: "network-only" },
  );
  const [isWorkflowDone, setIsWorkflowDone] = useState(false);
  const [isAddingBooking, setIsAddingBooking] = useState(false);
  const { setBookingText, getBookingText } = useBookingTextStore();
  const { tenantId, isVorsteuerabzug, hasCostCenters } = useTenant();
  const { sortOrderSet, setSortOrderSet, addSortOrder } =
    useSortingOrderStore();
  const [invoiceMode, setInvoiceMode] = useState<InvoiceModes>(
    InvoiceModes.Netto,
  );

  useEffect(() => {
    let isCancelled = false;

    const fetchData = async () => {
      try {
        setLoading(true);
        setLineItems(undefined);
        handleSetLineItems([]);
        handleSetOperations([]);

        const { data, error } = await fetchLineItems(invoice.invoiceId);

        if (isCancelled) {
          return;
        }

        if (error) {
          throw error;
        }

        if (!data) {
          setLoading(false);
          return;
        }

        const bookings = groupByBooking(
          data.lineItemDetails.map((lineItem: any) => ({
            lineItemId: lineItem.lineItemId,
            data: {
              lineItemDetails: { lineItem: lineItem.lineItem },
              invoiceDetails: { invoice },
            },
            tenantId,
          })),
        );

        for (const booking of bookings) {
          const bookingText = booking.description;
          const lineItemIds = booking.subRows.map((item: any) => item.id);

          const hasBookingText = getBookingText(booking.id);

          if (!hasBookingText) {
            setBookingText(lineItemIds.join("-"), {
              lineItemIds: lineItemIds,
              bookingText: bookingText,
            });
          }
        }

        const _lineItems = data.lineItemDetails.map((lineItem: any) => ({
          ...lineItem,
          ...lineItem.lineItem,
        }));

        setBookings(bookings);
        handleSetLineItems(_lineItems);
        setSortOrderSet(bookings);
        setLineItems(_lineItems);
      } catch (error: any) {
        if (isCancelled) {
          return;
        }
        setLineItems(undefined);
        handleSetLineItems([]);
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isCancelled = true;
    };
  }, [invoice.invoiceId]);

  useEffect(() => {
    if (invoice?.isNettoInvoice !== undefined) {
      setInvoiceMode(
        invoice.isNettoInvoice ? InvoiceModes.Netto : InvoiceModes.Brutto,
      );
    }
  }, [invoice]);

  useEffect(() => {
    if (tenantId && lineItemState.lineItems.length > 0 && lineItems) {
      const bookings = groupByBooking(
        lineItemState.lineItems
          .filter((lineItem) => !lineItem.deletedAt)
          .map((lineItem) => ({
            lineItemId: lineItem.lineItemId,
            data: {
              lineItemDetails: { lineItem },
              invoiceDetails: { invoice },
            },
            tenantId,
          })),
      );

      const sortedBookings = [...bookings].sort((a, b) => {
        const keyA = a.id;
        const keyB = b.id;

        const idsA = a.subRows.map((sr: BookingType) => sr.id).join("-");
        const idsB = b.subRows.map((sr: BookingType) => sr.id).join("-");

        const findIdx = (bookingKey: string, lineIds: string) => {
          const entry = sortOrderSet.find(
            (entry) =>
              entry.value[0] === bookingKey || entry.value[1] === lineIds,
          );
          return entry ? entry.key : Number.MAX_SAFE_INTEGER;
        };

        const idxA = findIdx(keyA, idsA);
        const idxB = findIdx(keyB, idsB);

        return idxA - idxB;
      });

      setBookings(sortedBookings);
    }
  }, [
    lineItemState.lineItems,
    tenantId,
    invoice,
    lineItems,
    ...Array.from(sortOrderSet.keys()),
  ]);

  useEffect(() => {
    const _lineItems = lineItemState.lineItems.filter(
      (lineItem) => !lineItem.deletedAt,
    );
    if (lineItems && _lineItems.length > 0) {
      onLineItemsChange(_lineItems);

      const bookings = groupByBooking(
        lineItemState.lineItems
          .filter((lineItem) => !lineItem.deletedAt)
          .map((lineItem) => ({
            lineItemId: lineItem.lineItemId,
            data: {
              lineItemDetails: { lineItem },
              invoiceDetails: { invoice },
            },
            tenantId,
          })),
      );

      setSortOrderSet(bookings);
    }
  }, [lineItemState.lineItems, onLineItemsChange, lineItems]);

  const visibleLineItems = useMemo(() => {
    return lineItemState.lineItems.filter((lineItem) => !lineItem.deletedAt);
  }, [lineItemState.lineItems]);

  const initialColumnVisibility = useMemo(
    () => ({
      invoiceNo: false,
      supplierId: false,
      isDuplicate: false,
      isBooking: false,
      accountReviewed: false,
      createdAt: false,
    }),
    [],
  );

  const columns = useMemo(() => {
    return _columns({
      invoiceMode,
      readOnly,
      disabled,
      isVorsteuerabzug: isVorsteuerabzug ?? false,
      showCostCenterColumn: hasCostCenters ?? false,
      handleUpdateBookingAmount,
      handleUpdateBookingNettoAmount,
      handleUpdateBookingVatRate,
      handleUpdateAccountId,
      handleUpdateTaxCodeId,
      handleUpdateCostCenterId,
      handleUpdateLineItems,
    });
  }, [
    invoiceMode,
    readOnly,
    disabled,
    isVorsteuerabzug,
    handleUpdateBookingAmount,
    handleUpdateBookingNettoAmount,
    handleUpdateBookingVatRate,
    handleUpdateAccountId,
    handleUpdateTaxCodeId,
  ]);

  const handleTotalAmountChanged = useCallback(
    (event: any) => {
      const amount = event.detail.value;

      if (bookings.length > 1) {
        return;
      }

      const lineItemIds = bookings.flatMap((booking) =>
        //@ts-ignore
        booking.subRows.map((subRow) => subRow.id),
      );

      handleUpdateBookingAmount(lineItemIds, amount);
    },
    [
      bookings,
      invoiceMode,
      handleUpdateBookingAmount,
      handleUpdateBookingNettoAmount,
    ],
  );

  useEffect(() => {
    window.addEventListener(
      EVENT_NAMES.TOTAL_AMOUNT_CHANGED,
      handleTotalAmountChanged,
    );

    return () => {
      window.removeEventListener(
        EVENT_NAMES.TOTAL_AMOUNT_CHANGED,
        handleTotalAmountChanged,
      );
    };
  }, [handleTotalAmountChanged]);

  const handleWorkflowDone = () => {
    setIsWorkflowDone(true);

    setTimeout(() => {
      const element = document.getElementById("invoice-details-panel");
      if (element) {
        element.scrollTop = element.scrollHeight;
      }
    }, 300);
  };

  const handleSuccess = async (
    incomingLineItems: LineItem[],
    operations: OperationAction[],
  ) => {
    const currentOperations = getOperations();
    const currentLineItems = getLineItems();
    const remainingOperations = currentOperations.filter(
      (operation) => !operations.includes(operation),
    );
    const resolvedLineItems = resolutionStrategy(
      currentLineItems,
      incomingLineItems,
      remainingOperations,
    );

    onLineItemsChange(
      resolvedLineItems.filter((lineItem) => !lineItem.deletedAt),
    );
    handleSetLineItems(resolvedLineItems);
    handleSetOperations(remainingOperations);
  };

  const handleAddBooking = () => {
    setIsAddingBooking((prev) => !prev);
  };

  const handleAddLineItem = (lineItem: Partial<LineItem>) => {
    _handleAddLineItem(lineItem);

    if (!lineItem.lineItemId) {
      return;
    }

    setBookingText(lineItem.lineItemId, {
      lineItemIds: [lineItem.lineItemId],
      bookingText: lineItem.bookingText ?? "",
    });

    addSortOrder(
      getBookingKey({
        accountId: lineItem.accountId ?? null,
        taxCodeId: lineItem.taxCodeId ?? null,
        costCenterId: lineItem.costCenterId ?? null,
        invoiceId: lineItem.invoiceId ?? "",
        createdAt: new Date().toISOString(),
        createdBy: LineItemCreatedBy.Manual,
        unlinked: null,
      }),
      [lineItem.lineItemId],
    );
  };

  const handleUnlinkLineItems = (lineItemIds: string[]) => {
    handleUnlink(lineItemIds);
  };

  if (loading || !lineItems) return null;

  if (!invoiceMode || (bookings.length === 0 && !isWorkflowDone)) {
    return (
      <div className="flex justify-center items-center h-[250px]">
        <InvoiceModeCard
          invoiceId={invoice.invoiceId}
          onSelectInvoiceMode={handleWorkflowDone}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full gap-2 mb-4">
      <div className="flex justify-between items-center">
        <TypographyLarge>{t("invoice_review.bookings")}</TypographyLarge>
        <ReviewToolBar readOnly={Boolean(readOnly)}>
          <Button
            variant={isAddingBooking ? "secondary" : "outline"}
            size="sm"
            onClick={handleAddBooking}
            disabled={readOnly}
          >
            <Plus width={16} height={16} className="mr-2" />
            {t("invoice_review.add_booking")}
          </Button>
        </ReviewToolBar>
      </div>
      <ValidationErrorMessage name="lineItems" />
      <ValidationRoot
        schema={lineItemsValidationSchema}
        values={visibleLineItems}
      >
        {Boolean(lineItems.length) && (
          <ValidationErrorMessage name="accountIds" />
        )}
      </ValidationRoot>
      <BookingsTableInstanceProvider
        columns={columns}
        tableName="invoice-bookings"
        tableData={bookings}
        initialColumnVisibility={initialColumnVisibility}
        clickableColumns={[]}
        footer={false}
      >
        <div className="h-full">
          <Table
            className="h-full overflow-x-auto"
            autoSize={false}
            rowValidationSchema={lineItemValidationSchema}
          />
        </div>
        <InvoiceBookingsActionBar
          invoiceData={invoice}
          isAddingBooking={isAddingBooking}
          lineItems={lineItemState.lineItems}
          invoiceMode={invoiceMode}
          onAddLineItem={handleAddLineItem}
          onDelete={handleDeleteLineItem}
          onUpdateAccountId={handleUpdateAccountId}
          onUpdateTaxCodeId={handleUpdateTaxCodeId}
          onUpdateCostCenterId={handleUpdateCostCenterId}
          onUnlink={handleUnlinkLineItems}
        />
      </BookingsTableInstanceProvider>
      <BackendSync
        invoiceId={invoice.invoiceId}
        operations={lineItemState.operations}
        onSuccess={handleSuccess}
        onLoading={onLoading}
      />
    </div>
  );
};

const resolutionStrategy = (
  currentLineItems: LineItem[],
  incomingLineItems: LineItem[],
  operations: OperationAction[],
) => {
  const currentMap = new Map(
    currentLineItems.map((item) => [item.lineItemId, item]),
  );
  const incomingMap = new Map(
    incomingLineItems.map((item) => [item.lineItemId, item]),
  );
  const operationsMap = new Map(
    operations.map((operation) => [operation.lineItemId, operation]),
  );

  const resolvedItems: LineItem[] = [];
  // Handle items that exist in both current and incoming
  for (const [lineItemId, currentItem] of Array.from(currentMap.entries())) {
    const incomingItem = incomingMap.get(lineItemId);

    if (incomingItem) {
      const currentOperation = operationsMap.get(lineItemId);

      // If no current operation exists, use incoming item as is
      if (!currentOperation) {
        resolvedItems.push(incomingItem);
        incomingMap.delete(lineItemId);
        continue;
      }

      // Keep current item if it has been modified locally
      const relevantValues = omit(incomingItem, [
        "lineItemId",
        "invoiceId",
        "amount",
        "vatRate",
        "accountId",
        "taxCodeId",
        "description",
        "costCenterId",
        "bookingText",
        "name",
      ]);

      // override the current item with the incoming item
      resolvedItems.push({ ...currentItem, ...relevantValues });
      incomingMap.delete(lineItemId);
    } else {
      // Item only exists in current - preserve it
      resolvedItems.push(currentItem);
    }
  }

  // Add remaining incoming items that don't exist in current
  for (const [_, incomingItem] of Array.from(incomingMap.entries())) {
    resolvedItems.push(incomingItem);
  }

  return resolvedItems;
};
